import React from 'react';
import {connect} from "react-redux";
import {addPaperToUnassigned, deleteCalendarSessionBySessionId, deleteSession, loginSucceeded} from "./redux/actions";
import {Redirect, Route, Switch} from "react-router-dom";
import AdminPage from "./AdminPage";
import SessionsPage from "./SessionsPage";
import CalendarPage from "./CalendarPage";
import LoginPage from "./LoginPage";
import {isPaperSession} from "./commons";
import {getLogout} from "./api";
import HomePage from "./HomePage";
import Header from "./components/Header";
import {Loader} from "./components/Loader";
import './App.css';
import SettingsPage from "./SettingsPage";
import {ConnectedRouter} from "connected-react-router";
import {history} from "./redux/store";
import ForgotPasswordPage from "./ForgotPasswordPage";

// A wrapper for <Route> that redirects to the login
// screen if you're not yet authenticated.
function PrivateRoute({children, ...rest}) {
    const {isAuthenticated} = {...rest};
    return (
        <Route
            {...rest}
            render={({location}) =>
                isAuthenticated() ? (
                    children
                ) : (
                    <Redirect
                        to={{
                            pathname: "/login",
                            state: {from: location}
                        }}
                    />
                )
            }
        />
    );
}

class App extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            title: '',
            activeTab: '',
        };

        this.updateHeader = this.updateHeader.bind(this);
        this.removeSession = this.removeSession.bind(this);
        this.isAuthenticated = this.isAuthenticated.bind(this);
        this.handleLogout = this.handleLogout.bind(this);
    }

    updateHeader(newTitle, tabName) {
        this.setState((state, props) => {
            return {title: newTitle, activeTab: tabName}
        })
    }

    /**
     * Remove the session specified by session_id. It also moves papers back to the list of available papers.
     * @param session_id
     */
    removeSession(session_id) {
        const {sessions} = this.props;
        const session = sessions[session_id];

        if (isPaperSession(session)) {
            // If the session has papers move them to the list of available papers before actually remove it
            session.papers.forEach((paper, index) => this.props.addPaperToUnassigned(paper));
        }

        // Remove session id from sessions list
        this.props.deleteSession(session_id);

        this.props.deleteCalendarSessionBySessionId(session_id);
    }

    filterPaperSessions(sessions) {
        const ret = {};
        for (const session_id in sessions) {
            const session = sessions[session_id];
            if (session.type === 'Paper') ret[session_id] = session;
        }
        return ret;
    }

    componentDidMount() {
        const {user, loginSucceeded} = this.props;
        // If we have successfully retrieved a user from the sessionStorage
        if (user) loginSucceeded(user);
    }

    isAuthenticated() {
        return Boolean(this.props.user)
    }

    handleLogout(event) {
        const callback = () => {
            sessionStorage.removeItem('user');
            window.location.replace("/");   // Redirects to root page
        };
        getLogout(this.props.user.api_token, callback);
    }

    render() {
        const {title, activeTab} = this.state;
        const {sessionTypes, sessions, areSessionsLoaded, user} = this.props;

        const paperSessions = this.filterPaperSessions(sessions);

        if (this.isAuthenticated() && !areSessionsLoaded) return <div id="outer"><Loader/></div>;

        return (
            <ConnectedRouter history={history}>
                <div id="outer">
                    <Header title={title}
                            activeTab={activeTab}
                            isAuthenticated={this.isAuthenticated}
                            user={user}
                            handleLogout={this.handleLogout}/>

                    {/*
                    A <Switch> looks through all its children <Route>
                    elements and renders the first one whose path
                    matches the current URL. Use a <Switch> any time
                    you have multiple routes, but you want only one
                    of them to render at a time
                    */}
                    <Switch>
                        <Route path="/login">
                            <LoginPage/>
                        </Route>

                        <Route path="/password/forgot">
                            <ForgotPasswordPage/>
                        </Route>

                        <PrivateRoute path="/sessions" isAuthenticated={this.isAuthenticated}>
                            <SessionsPage updateHeader={this.updateHeader}
                                          sessionTypes={sessionTypes}
                                          sessions={paperSessions}
                                          removeSession={this.removeSession}
                                          user={user}/>
                        </PrivateRoute>

                        <PrivateRoute path="/calendar" isAuthenticated={this.isAuthenticated}>
                            <CalendarPage updateHeader={this.updateHeader}
                                          sessionTypes={sessionTypes}
                                          sessions={sessions}
                                          removeSession={this.removeSession}
                                          user={user}/>
                        </PrivateRoute>

                        <PrivateRoute path="/admin" isAuthenticated={this.isAuthenticated}>
                            <AdminPage updateHeader={this.updateHeader}
                                       user={user}/>
                        </PrivateRoute>

                        <PrivateRoute path="/settings" isAuthenticated={this.isAuthenticated}>
                            <SettingsPage updateHeader={this.updateHeader}
                                          user={user}/>
                        </PrivateRoute>

                        <Route path="/">
                            <HomePage/>
                        </Route>
                    </Switch>
                </div>
            </ConnectedRouter>
        )
    }
}

const mapStateToProps = state => {
    return {
        user: state.auth.user,
        sessionTypes: state.sessions.sessionTypes,
        sessions: state.sessions.byIds,
        areSessionsLoaded: state.sessions.areSessionsLoaded,
        sessionColors: state.sessions.sessionColors
    }
};

export default connect(
    mapStateToProps,
    {addPaperToUnassigned, deleteSession, deleteCalendarSessionBySessionId, loginSucceeded}
)(App);
